import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingBag, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mediaUrl = (p) => (!p ? '' : p.startsWith('http') ? p : `${API_URL}${p}`);

// Public member storefront — customers buy via Stripe Checkout; money routes
// to the member's connected Stripe account (minus the platform fee).
export default function Storefront() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/store/${userId}`);
        setStore(res.data);
      } catch { /* not found state below */ } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  // Returning from Stripe Checkout — poll until the payment is confirmed.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      if (tries++ > 8) return;
      try {
        const res = await axios.get(`${API_URL}/api/store/checkout-status/${sessionId}`);
        if (res.data.payment_status === 'paid') { setPaid(true); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams]);

  const buy = async (product) => {
    setBuying(product.id);
    try {
      const res = await axios.post(`${API_URL}/api/store/${userId}/checkout`, {
        product_id: product.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setBuying(null);
    }
  };

  const accent = store?.member?.microsite_accent_color || '#D4A24A';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4A24A] animate-spin" />
      </div>
    );
  }
  if (!store) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center text-white/60">Store not found.</div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white" data-testid="storefront-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        {paid && (
          <div className="mb-8 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-6 py-4 flex items-center gap-3" data-testid="storefront-paid-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <p className="text-sm text-emerald-300">Payment received — thank you! You'll hear from {store.member.name} shortly.</p>
          </div>
        )}

        <div className="flex items-center gap-4 mb-2">
          {store.member.profile_photo && (
            <img src={mediaUrl(store.member.profile_photo)} alt={store.member.name} className="w-16 h-16 rounded-2xl object-cover ring-2" style={{ '--tw-ring-color': `${accent}80` }} />
          )}
          <div>
            <h1 className="font-playfair text-3xl">{store.member.name}'s Store</h1>
            {store.member.microsite_tagline && <p className="text-sm" style={{ color: accent }}>{store.member.microsite_tagline}</p>}
          </div>
        </div>
        <Link to={`/member/${userId}`} className="text-xs text-white/40 hover:text-white/70 transition" data-testid="storefront-profile-link">
          View full profile →
        </Link>

        <div className="mt-10 space-y-4">
          {store.products.length === 0 ? (
            <div className="text-center py-16 text-white/40">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No products listed yet — check back soon.</p>
            </div>
          ) : (
            store.products.map((p) => (
              <div key={p.id} className="bg-[#141416] border border-white/[0.08] rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap" data-testid={`storefront-product-${p.id}`}>
                <div className="min-w-0">
                  <h3 className="font-medium text-lg">{p.name}</h3>
                  {p.description && <p className="text-sm text-white/50 mt-1">{p.description}</p>}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="font-playfair text-2xl" style={{ color: accent }}>${p.price.toFixed(2)}</span>
                  <button
                    onClick={() => buy(p)}
                    disabled={buying === p.id}
                    className="px-5 py-2.5 text-black text-sm font-semibold rounded-lg hover:brightness-110 transition disabled:opacity-60"
                    style={{ backgroundColor: accent }}
                    data-testid={`storefront-buy-${p.id}`}
                  >
                    {buying === p.id ? 'Redirecting…' : 'Buy now'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <p className="mt-12 text-center text-[11px] text-white/30">
          Payments are processed securely by Stripe. Powered by{' '}
          <Link to="/" className="text-[#D4A24A]/70 hover:underline">WeMasterminds</Link>.
        </p>
      </div>
    </div>
  );
}
